<script setup>
import { computed } from 'vue';
import UploadForm from './components/UploadForm.vue';
import GrowerFilterPanel from './components/GrowerFilterPanel.vue';
import ReportSummaryPanel from './components/ReportSummaryPanel.vue';
import CollapsibleSection from './components/CollapsibleSection.vue';
import FlagTable from './components/FlagTable.vue';
import ChartGrid from './components/ChartGrid.vue';
import { useSoilIngest } from './composables/useSoilIngest.js';
import { useGrowerFilter } from './composables/useGrowerFilter.js';
import { useCollapsibleSections } from './composables/useCollapsibleSections.js';
import { groupHasData } from './utils/dataAvailability.js';
import { macroNutrientColumns, soilCharacteristicsColumns, boronSulfurColumns, optionalPanelSections } from './utils/tableColumnSpecs.js';

const props = defineProps({
	csrfToken: { type: String, default: '' },
	csrfParam: { type: String, default: '' }
});

const { loading, allSamples, lastOptimumRanges, reportMeta, validation, ingestFile, loadExample } = useSoilIngest({
	csrfToken: props.csrfToken,
	csrfParam: props.csrfParam
});

const { selectedGrower, growers, filteredSamples, selectGrower, resetFilter, growerKey } = useGrowerFilter(allSamples);
const { collapsed, collapseAll, toggle } = useCollapsibleSections();

function onIngestSuccess() {
	resetFilter();
	collapseAll();
}

function onSubmitFile(file) {
	ingestFile(file, onIngestSuccess);
}

function onLoadExample() {
	loadExample(onIngestSuccess);
}

const macroNutrientCols = computed(() => macroNutrientColumns(lastOptimumRanges.value));
const soilCharacteristicsCols = computed(() => soilCharacteristicsColumns(lastOptimumRanges.value));
const boronSulfurCols = computed(() => boronSulfurColumns(lastOptimumRanges.value));
const optionalPanels = computed(() => optionalPanelSections());

const footnoteText = computed(() => {
	if (!filteredSamples.value.length) return '';
	const src = lastOptimumRanges.value.source || 'unknown';
	return ' source: ' + src +
		'. Sodium (ppm), CEC, % base saturation, and the Turf Elite Plus add-on panels (soil physical/health, saturated-paste extract, water-soluble nutrients, H3A extractable nutrients, total combustion, and profile nitrogen by depth) are shown without Low/Optimum/High shading — the ingestion schema doesn\'t currently return thresholds for those fields.';
});
</script>

<template>
	<UploadForm :loading="loading" @submit-file="onSubmitFile" @load-example="onLoadExample" />

	<div class="tnt-soil-report">
		<GrowerFilterPanel
			:growers="growers"
			:selected-grower="selectedGrower"
			:grower-key="growerKey"
			@select="selectGrower"
		/>

		<ReportSummaryPanel
			:report-meta="reportMeta"
			:validation="validation"
			:collapsed="collapsed.reportSummary"
			@update:collapsed="toggle('reportSummary')"
		/>

		<CollapsibleSection
			title="Phosphorus, Potassium &amp; Micronutrients (ppm)"
			:visible="groupHasData(filteredSamples, macroNutrientCols)"
			:model-value="collapsed.macroNutrient"
			@update:model-value="toggle('macroNutrient')"
		>
			<FlagTable :samples="filteredSamples" :columns="macroNutrientCols" />
		</CollapsibleSection>

		<CollapsibleSection
			title="Soil Characteristics &amp; Base Saturation"
			:visible="groupHasData(filteredSamples, soilCharacteristicsCols)"
			:model-value="collapsed.soilCharacteristics"
			@update:model-value="toggle('soilCharacteristics')"
		>
			<FlagTable :samples="filteredSamples" :columns="soilCharacteristicsCols" />
		</CollapsibleSection>

		<CollapsibleSection
			title="Boron &amp; Sulfur (ppm)"
			:visible="groupHasData(filteredSamples, boronSulfurCols)"
			:model-value="collapsed.boronSulfur"
			@update:model-value="toggle('boronSulfur')"
		>
			<FlagTable :samples="filteredSamples" :columns="boronSulfurCols" />
		</CollapsibleSection>

		<CollapsibleSection
			v-for="panel in optionalPanels"
			:key="panel.id"
			:title="panel.title"
			:visible="groupHasData(filteredSamples, panel.columns)"
			:model-value="collapsed[panel.id]"
			@update:model-value="toggle(panel.id)"
		>
			<FlagTable :samples="filteredSamples" :columns="panel.columns" striped />
		</CollapsibleSection>

		<ChartGrid
			:samples="filteredSamples"
			:optimum-ranges="lastOptimumRanges"
			:selected-grower="selectedGrower"
			:collapsed="collapsed.charts"
			@update:collapsed="toggle('charts')"
		/>

		<div v-if="footnoteText" class="tnt-footnote"><b>Optimum ranges</b>{{ footnoteText }}</div>
	</div>
</template>
